import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Candidate, JobOffer } from '../../models/job';
import { CandidateSidebar } from './CandidateSidebar';
import { X, ZoomOut, RotateCcw } from 'lucide-react';
import { Tooltip } from '../ui/tooltip';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea, Label } from 'recharts';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { Button } from '../ui/atoms/Button';
import { isAssessedStageSemantic } from '../../utils/pipeline-stages';
import { useJobPipeline } from '../../hooks/useJobOffers';
import { useStageTypeDisplayLabel } from '../../hooks/useStageDisplayLabel';

interface CandidatesDashboardProps {
  candidates: Candidate[];
  job: JobOffer;
  techScores: Record<string, number | null>;
  softSkillsScores: Record<string, number | null>;
  overallScores: Record<string, number | null>;
  onStatusUpdate?: () => void;
  /** Closes the performance dashboard and returns to the candidate list. */
  onClose?: () => void;
}

interface CandidatePoint {
  id: string;
  name: string;
  firstName: string;
  techScore: number;
  softScore: number;
  overallScore: number | null;
  status: string | undefined;
  candidate: Candidate;
  // For display purposes
  x: number;
  y: number;
  color: string;
}

export const CandidatesDashboard: React.FC<CandidatesDashboardProps> = ({
  candidates,
  job,
  techScores,
  softSkillsScores,
  overallScores,
  onStatusUpdate,
  onClose,
}) => {
  const { t } = useTranslation(['candidates', 'job', 'common']);
  const { data: pipeline } = useJobPipeline(job.id);
  const getStageTypeLabel = useStageTypeDisplayLabel(pipeline?.stages);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [isCandidateSidebarOpen, setIsCandidateSidebarOpen] = useState<boolean>(false);
  const [hoveredCandidateId, setHoveredCandidateId] = useState<string | null>(null);
  const [xDomain, setXDomain] = useState<[number, number]>([0, 100]);
  const [yDomain, setYDomain] = useState<[number, number]>([0, 100]);
  
  // Selection state for drag-to-zoom
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to get color based on status
  const getStatusColor = (status: string | undefined) => {
    if (status === 'accepted') return '#dcfce7';
    if (status === 'rejected') return '#FF98B54D';
    if (status === 'interviewed') return '#C0E1FF80';
    if (status === 'hired') return '#D9ADFF66';
    return '#171D270D';
  };

  // Filter and prepare data for Recharts with jitter for overlapping points
  const candidatePoints = useMemo((): CandidatePoint[] => {
    const candidatesWithScores = candidates.filter((c) =>
      isAssessedStageSemantic(c.pipelineStageSemantic)
    );

    const basePoints = candidatesWithScores
      .filter(candidate =>
        techScores[candidate.id] !== null &&
        techScores[candidate.id] !== undefined &&
        softSkillsScores[candidate.id] !== null &&
        softSkillsScores[candidate.id] !== undefined
      )
      .map(candidate => {
        const techScore = techScores[candidate.id]!;
        const softScore = softSkillsScores[candidate.id]!;
        
        return {
        id: candidate.id,
        name: candidate.talentName,
        firstName: candidate.talentName.split(' ')[0],
          techScore: techScore,
          softScore: softScore,
          x: softScore,
          y: techScore,
        overallScore: overallScores[candidate.id],
        status: candidate.pipelineStageSemantic,
          candidate: candidate,
          color: getStatusColor(candidate.pipelineStageSemantic)
        };
      });

    // Apply smart jitter to prevent overlapping while keeping positions accurate
    const jitterAmount = 1.5; // Small jitter (1.5 points max)
    const processedPoints: CandidatePoint[] = [];
    
    basePoints.forEach((point) => {
      let finalX = point.x;
      let finalY = point.y;
      
      // Check for overlaps with already processed points
      let hasOverlap = true;
      let attempts = 0;
      const maxAttempts = 10;
      
      while (hasOverlap && attempts < maxAttempts) {
        hasOverlap = false;
        
        for (const existing of processedPoints) {
          const distance = Math.sqrt(
            Math.pow(finalX - existing.x, 2) + 
            Math.pow(finalY - existing.y, 2)
          );
          
          // If points are too close (within 2 units), apply jitter
          if (distance < 2) {
            hasOverlap = true;
            // Apply small random offset
            const angle = Math.random() * 2 * Math.PI;
            finalX = point.softScore + Math.cos(angle) * jitterAmount * (attempts + 1) * 0.3;
            finalY = point.techScore + Math.sin(angle) * jitterAmount * (attempts + 1) * 0.3;
            break;
          }
        }
        
        attempts++;
      }
      
      processedPoints.push({
        ...point,
        x: finalX,
        y: finalY
      });
    });
    return processedPoints;
  }, [candidates, techScores, softSkillsScores, overallScores]);

  const handleCandidateClick = (data: CandidatePoint) => {
    setSelectedCandidateId(data.id);
    setIsCandidateSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsCandidateSidebarOpen(false);
    setSelectedCandidateId(null);
  };

  const selectedCandidate = useMemo(() => {
    if (!selectedCandidateId) {
      return null;
    }

    const candidateEntry = candidatePoints.find((point) => point.id === selectedCandidateId);
    return candidateEntry ? candidateEntry.candidate : null;
  }, [candidatePoints, selectedCandidateId]);

  useEffect(() => {
    if (!selectedCandidateId) {
      return;
    }

    const existsInPoints = candidatePoints.some((point) => point.id === selectedCandidateId);
    if (!existsInPoints) {
      setIsCandidateSidebarOpen(false);
      setSelectedCandidateId(null);
    }
  }, [candidatePoints, selectedCandidateId]);

  // Chart area dimensions (matching the margin in ScatterChart)
  const chartMargin = { top: 20, right: 30, bottom: 70, left: 50 };
  const chartHeightValue = 550; // Must match ResponsiveContainer height

  // Convert pixel position to data coordinates
  const pixelToData = useCallback((pixelX: number, pixelY: number, currentXDomain: [number, number], currentYDomain: [number, number]) => {
    if (!chartContainerRef.current) return null;
    
    const containerRect = chartContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    
    // The actual plot area dimensions (inside the margins)
    const plotWidth = containerWidth - chartMargin.left - chartMargin.right;
    const plotHeight = chartHeightValue - chartMargin.top - chartMargin.bottom;
    
    // Position relative to the plot area origin (top-left of plot area)
    const plotX = pixelX - chartMargin.left;
    const plotY = pixelY - chartMargin.top;
    
    // Clamp to valid range
    const clampedPlotX = Math.max(0, Math.min(plotWidth, plotX));
    const clampedPlotY = Math.max(0, Math.min(plotHeight, plotY));
    
    // Calculate percentage position within plot area (0 to 1)
    const xPercent = clampedPlotX / plotWidth;
    const yPercent = clampedPlotY / plotHeight;
    
    // Convert to data coordinates
    const xRange = currentXDomain[1] - currentXDomain[0];
    const yRange = currentYDomain[1] - currentYDomain[0];
    
    // X goes left to right (0% = xMin, 100% = xMax)
    // Y goes top to bottom in pixels, but high to low in data (0% = yMax, 100% = yMin)
    const dataX = currentXDomain[0] + xPercent * xRange;
    const dataY = currentYDomain[1] - yPercent * yRange;
    
    return {
      x: dataX,
      y: dataY
    };
  }, [chartMargin, chartHeightValue]);

  // Mouse handlers for selection
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!chartContainerRef.current) return;
    
    // Don't start selection if clicking on a candidate dot or SVG element
    const target = e.target as HTMLElement;
    if (target.tagName === 'circle' || target.closest('circle')) {
      return; // Let the dot handle the click
    }
    
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Only start selection if within chart area
    if (x >= chartMargin.left && x <= rect.width - chartMargin.right &&
        y >= chartMargin.top && y <= rect.height - chartMargin.bottom) {
      e.preventDefault();
      setIsSelecting(true);
      setSelectionStart({ x, y });
      setSelectionEnd({ x, y });
    }
  }, [chartMargin]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !chartContainerRef.current) return;
    
    e.preventDefault();
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = Math.max(chartMargin.left, Math.min(rect.width - chartMargin.right, e.clientX - rect.left));
    const y = Math.max(chartMargin.top, Math.min(rect.height - chartMargin.bottom, e.clientY - rect.top));
    
    setSelectionEnd({ x, y });
  }, [isSelecting, chartMargin]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Don't handle selection if clicking on a candidate dot
    const target = e.target as HTMLElement;
    if (target.tagName === 'circle' || target.closest('circle')) {
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }
    
    if (!isSelecting || !selectionStart || !selectionEnd || !chartContainerRef.current) {
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }
    
    e.preventDefault();
    
    // Calculate selection size in pixels
    const pixelWidth = Math.abs(selectionEnd.x - selectionStart.x);
    const pixelHeight = Math.abs(selectionEnd.y - selectionStart.y);
    
    // Only zoom if selection is large enough (at least 20px)
    if (pixelWidth > 20 && pixelHeight > 20) {
      // Use current domain values at the time of selection
      const startData = pixelToData(selectionStart.x, selectionStart.y, xDomain, yDomain);
      const endData = pixelToData(selectionEnd.x, selectionEnd.y, xDomain, yDomain);
      
      if (startData && endData && chartContainerRef.current) {
        // Calculate the exact bounds of the selection in data coordinates
        const selectionXMin = Math.min(startData.x, endData.x);
        const selectionXMax = Math.max(startData.x, endData.x);
        const selectionYMin = Math.min(startData.y, endData.y);
        const selectionYMax = Math.max(startData.y, endData.y);
        
        // Calculate center of selection
        const centerX = (selectionXMin + selectionXMax) / 2;
        const centerY = (selectionYMin + selectionYMax) / 2;
        
        // Calculate selection size in data coordinates
        const selectionDataWidth = selectionXMax - selectionXMin;
        const selectionDataHeight = selectionYMax - selectionYMin;
        
        // Get chart plot area dimensions (excluding margins)
        const containerRect = chartContainerRef.current.getBoundingClientRect();
        const plotWidth = containerRect.width - chartMargin.left - chartMargin.right;
        const plotHeight = chartHeightValue - chartMargin.top - chartMargin.bottom;
        
        // Calculate chart aspect ratio (plot area)
        const chartAspectRatio = plotWidth / plotHeight;
        
        // Calculate selection aspect ratio in data space
        // Note: We need to account for the data range aspect ratio
        const currentXRange = xDomain[1] - xDomain[0];
        const currentYRange = yDomain[1] - yDomain[0];
        const dataAspectRatio = (selectionDataWidth / currentXRange) / (selectionDataHeight / currentYRange);
        
        // Add padding (10% on each side)
        const paddingFactor = 0.1;
        let paddedWidth = selectionDataWidth * (1 + paddingFactor * 2);
        let paddedHeight = selectionDataHeight * (1 + paddingFactor * 2);
        
        // Adjust to match chart aspect ratio
        // If selection is wider than chart aspect, expand height
        // If selection is taller than chart aspect, expand width
        const targetDataAspectRatio = chartAspectRatio * (currentXRange / currentYRange);
        
        if (dataAspectRatio > targetDataAspectRatio) {
          // Selection is wider relative to chart - expand height
          paddedHeight = paddedWidth / targetDataAspectRatio;
        } else {
          // Selection is taller relative to chart - expand width
          paddedWidth = paddedHeight * targetDataAspectRatio;
        }
        
        // Calculate new domain centered on selection
        const halfWidth = paddedWidth / 2;
        const halfHeight = paddedHeight / 2;
        
        let newXMin = centerX - halfWidth;
        let newXMax = centerX + halfWidth;
        let newYMin = centerY - halfHeight;
        let newYMax = centerY + halfHeight;
        
        // Clamp to valid bounds (0-100) and adjust to maintain centering
        if (newXMin < 0) {
          const offset = -newXMin;
          newXMin = 0;
          newXMax = Math.min(100, newXMax + offset);
        }
        if (newXMax > 100) {
          const offset = newXMax - 100;
          newXMax = 100;
          newXMin = Math.max(0, newXMin - offset);
        }
        
        if (newYMin < 0) {
          const offset = -newYMin;
          newYMin = 0;
          newYMax = Math.min(100, newYMax + offset);
        }
        if (newYMax > 100) {
          const offset = newYMax - 100;
          newYMax = 100;
          newYMin = Math.max(0, newYMin - offset);
        }
        
        // Ensure we have a valid range (at least 1 unit)
        if (newXMax - newXMin >= 1 && newYMax - newYMin >= 1) {
          setXDomain([newXMin, newXMax]);
          setYDomain([newYMin, newYMax]);
        }
      }
    }
    
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, [isSelecting, selectionStart, selectionEnd, pixelToData, xDomain, yDomain]);

  const handleMouseLeave = useCallback(() => {
    if (isSelecting) {
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
    }
  }, [isSelecting]);

  // Get selection box style
  const getSelectionBoxStyle = () => {
    if (!isSelecting || !selectionStart || !selectionEnd) return null;
    
    const left = Math.min(selectionStart.x, selectionEnd.x);
    const top = Math.min(selectionStart.y, selectionEnd.y);
    const width = Math.abs(selectionEnd.x - selectionStart.x);
    const height = Math.abs(selectionEnd.y - selectionStart.y);
    
    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  };

  const handleZoomOut = () => {
    const centerX = (xDomain[0] + xDomain[1]) / 2;
    const centerY = (yDomain[0] + yDomain[1]) / 2;
    const rangeX = Math.min((xDomain[1] - xDomain[0]) * 1.5, 100) / 2;
    const rangeY = Math.min((yDomain[1] - yDomain[0]) * 1.5, 100) / 2;

    const newXMin = Math.max(0, centerX - rangeX);
    const newXMax = Math.min(100, centerX + rangeX);
    const newYMin = Math.max(0, centerY - rangeY);
    const newYMax = Math.min(100, centerY + rangeY);

    setXDomain([newXMin, newXMax]);
    setYDomain([newYMin, newYMax]);
  };

  const handleResetZoom = () => {
    setXDomain([0, 100]);
    setYDomain([0, 100]);
  };

  const isZoomed = xDomain[0] !== 0 || xDomain[1] !== 100 || yDomain[0] !== 0 || yDomain[1] !== 100;

  const formatTick = (value: number) => Math.round(value).toString();

  // GTM tracking for opening Meta CV from chart point
  const openMetaCVAttrs = getGTMAttributes(createGTMTracking('OPEN_META_CV', { origin: 'candidates_dashboard' }));

  // Custom dot shape with label
  const CustomDot = (props: { cx?: number; cy?: number; payload?: CandidatePoint }) => {
    const { cx = 0, cy = 0, payload } = props;
    if (!payload) return null;

    const isHovered = hoveredCandidateId === payload.id;
    const radius = isHovered ? 14 : 10;
    const labelSize = isHovered ? 12 : 10;

    return (
      <g>
        {isHovered && (
          <circle
            cx={cx}
            cy={cy}
            r={radius + 4}
            fill={payload.color}
            fillOpacity={0.3}
            stroke="none"
          />
        )}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill={payload.color}
          stroke="white"
          strokeWidth={isHovered ? 3 : 2}
          className="cursor-pointer transition-all"
          {...openMetaCVAttrs}
          onClick={(e) => {
            e.stopPropagation();
            handleCandidateClick(payload);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseEnter={() => setHoveredCandidateId(payload.id)}
          onMouseLeave={() => setHoveredCandidateId(null)}
          style={{ filter: isHovered ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
        />
        {isHovered && (
          <text
            x={cx}
            y={cy - radius - 6}
            textAnchor="middle"
            fontSize={labelSize}
            fill="#374151"
            fontWeight="700"
            className="pointer-events-none select-none"
            style={{ textShadow: '0 1px 2px white, 0 -1px 2px white, 1px 0 2px white, -1px 0 2px white' }}
          >
            {payload.name}
          </text>
        )}
      </g>
    );
  };

  const closeButton = onClose ? (
    <Button
      type="icon"
      intent="muted"
      size="sm"
      onClick={onClose}
      aria-label={t('candidates:performance_dashboard.close_aria')}
      title={t('common:actions.close')}
      label={t('candidates:performance_dashboard.close_aria')}
      icon={<X />}
      className="shrink-0"
    />
  ) : null;

  if (candidatePoints.length === 0) {
    return (
      <div className="bg-white px-6 pb-6 pt-2 h-full flex flex-col" data-tour="candidates-performance-chart">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center space-x-3">
            <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20h16M4 16l4-4 4 4 8-8" />
            </svg>
            <h3 className="text-lg font-semibold text-marine-700">{t('candidates:performance_dashboard.title')}</h3>
          </div>
          {closeButton}
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 mb-2">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">{t('candidates:performance_dashboard.empty_message')}</p>
            <p className="text-gray-400 text-xs mt-1">{t('candidates:performance_dashboard.empty_hint')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white px-6 pb-6 pt-2 h-full flex flex-col" data-tour="candidates-performance-chart">
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex space-x-3 items-center">
            <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20h16M4 16l4-4 4 4 8-8" />
            </svg>
            <h3 className="text-lg font-semibold text-marine-700">{t('candidates:performance_dashboard.title')}</h3>
            <Tooltip 
              content={t('candidates:performance_dashboard.tooltip')}
              variant="bottom"
              className="w-64 !whitespace-normal"
            >
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-help">
                <span className="text-xs font-semibold text-gray-600">i</span>
              </div>
            </Tooltip>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-soft rounded-full mr-2"></div>
                <span>{getStageTypeLabel('accepted')}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-soft rounded-full mr-2"></div>
                <span>{getStageTypeLabel('rejected')}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-soft rounded-full mr-2"></div>
                <span>{getStageTypeLabel('interviewed')}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-soft rounded-full mr-2"></div>
                <span>{getStageTypeLabel('hired')}</span>
              </div>
            </div>
            {closeButton}
          </div>
        </div>

        <div className="flex-1 min-h-0 w-full">
          <div className="flex flex-col items-center w-full">
            <div
              ref={chartContainerRef}
              className="relative select-none w-full"
              style={{ height: chartHeightValue, cursor: isSelecting ? 'crosshair' : 'crosshair' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            >
              {/* Selection Box Overlay - transparent with dashed border */}
              {isSelecting && selectionStart && selectionEnd && (
                <div
                  className="absolute pointer-events-none z-20"
                  style={{
                    ...getSelectionBoxStyle(),
                    border: '2px dashed #3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  }}
                />
              )}
              
              {/* Zoom Controls */}
              <div className="absolute top-4 right-4 z-10 flex gap-2">
                {isZoomed && (
                  <>
                    <Button
                      type="outlined"
                      intent="secondary"
                      size="sm"
                      onClick={handleZoomOut}
                      title={t('candidates:performance_dashboard.zoom_out')}
                      label={t('candidates:performance_dashboard.zoom_out')}
                      icon={<ZoomOut />}
                    />
                    <Button
                      type="outlined"
                      intent="secondary"
                      size="sm"
                      onClick={handleResetZoom}
                      title={t('candidates:performance_dashboard.reset_view')}
                      label={t('candidates:performance_dashboard.reset')}
                      icon={<RotateCcw />}
                    />
                  </>
                )}
              </div>
              

            
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={chartMargin}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  
                  <XAxis
                    type="number"
                    dataKey="x"
                    name="Soft Skills"
                    domain={xDomain}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickCount={6}
                    allowDataOverflow={true}
                    unit="%"
                    tickFormatter={formatTick}
                  >
                    <Label
                      value={t('candidates:performance_dashboard.axis_soft_skills')}
                      position="insideBottom"
                      offset={-10}
                      style={{ fontSize: 14, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
                    />
                  </XAxis>
                  
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="Tech Skills"
                    domain={yDomain}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickCount={6}
                    allowDataOverflow={true}
                    unit="%"
                    tickFormatter={formatTick}
                  >
                    <Label
                      value={t('candidates:performance_dashboard.axis_technical')}
                      angle={-90}
                      position="insideLeft"
                      offset={11}
                      
                      style={{ fontSize: 14, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
                    />
                  </YAxis>
                  
                  {/* High performers area (top-right quadrant) - green zone */}
                  <ReferenceArea
                    x1={60}
                    x2={100}
                    y1={60}
                    y2={100}
                    fill="#22c55e"
                    fillOpacity={0.1}
                    stroke="none"
                  />

                  <Scatter
                    data={candidatePoints}
                    fill="#8884d8"
                    shape={<CustomDot />}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>  
          </div>
        </div>
      </div>

      {/* Candidate Sidebar */}
      {selectedCandidate && (
        <CandidateSidebar
          candidate={selectedCandidate}
          isOpen={isCandidateSidebarOpen}
          onClose={handleCloseSidebar}
          job={job}
          onStatusUpdate={onStatusUpdate}
        />
      )}
    </>
  );
};

